/**
 * 
 * this is a TokkenR Blockchain client that interfaces with our own node
 * 
 * 
 * BlockchainWrapper adds a lot of functionalities like param validation & caching of API responses
 * cf https://github.com/runonbitcoin/run-sdk/blob/main/lib/plugins/blockchain-wrapper.js
 * 
 */

import { TOKKENR_NODE_HOST_URL } from "../config"
import { address_to_script, getLog, scriptHex_to_address, script_to_scripthash } from "../helpers/helpers"

const Run = require("runlib")
const BlockchainWrapper = Run.plugins.BlockchainWrapper

/**
 * usage: 
 *      import TokkenrBlockchain from "./TokkenrBlockchain.js"
 *      const tokkenrBlockchain = new TokkenrBlockchain()
 * 
 * BEWARE THE NODE DOES NOT INDEXES BSV ADDRESSES SO IF YOU TRY TO RETRIEVE UTXOS FROM A PURSE IT WILL ANSWER AN EMPTY ARRAY
 */
class TokkenrBlockchain extends BlockchainWrapper {

    // --------------------------------------------------------------------------
    // constructor
    // --------------------------------------------------------------------------

    /**
     * options: not used for now, but can be used in the future to configure
     * 
     * note: no need to handle param validation or caching because BlockchainWrapper does it already
     */
    constructor(options = {}) {
        super()

        this.DEBUG = true
        this.classname = "TokkenrBlockchain: "
        const log = getLog(this.classname)

        // to avoid the error "Blockchain mismatch" we must define .api, .apiKey & .network 
        // in the same way as we define them in the Run() object
        this.api = options.api || "tokkenr"

        this.network = options.network || "main"
        if (this.network !== "main") {
            throw new Error("only the network 'main' is supported")
        }

        // we use the run-db client for all fetch to the server
        this.host_url = TOKKENR_NODE_HOST_URL
        this.rundb_client = new Run.plugins.RunDB(this.host_url)
    }

    async broadcast(rawtx) {
        const bsvtx = new bsv.Transaction(rawtx)
        const txhash = bsvtx.hash
        const function_id = this.classname + "broadcast(): "
        const log = getLog(function_id)

        // if (this.DEBUG) log("DEBUG, rawtx=", rawtx)
        log("with txhash=", txhash, "rawtx=", rawtx)

        const start_time = Date.now()
        const res = await this.rundb_client.broadcast(rawtx)
        log("finished in", (Date.now() - start_time), "ms")

        return res
    }


    async fetch(txid, force = false) {
        const log = getLog(this.classname, "fetch()")
        if (this.DEBUG) log("with txid=" + txid + " (" + typeof txid + "), force=" + force)

        // unsure if we should use the 'force' param ? if yes, we could use this.unwrappedFetch(txid, force)

        let result
        let pullError = null
        try {
            result = await this.rundb_client.pull("tx://" + txid)
        } catch (e) {
            pullError = e
            result = undefined
        }
        if (result !== undefined && result !== null) {
            return result
        }

        // refetch raw response to diagnose the issue
        let rawText = ""
        let rawStatus = -1
        try {
            const url = this.host_url + "/tx/" + txid
            const rawRes = await fetch(url)
            rawStatus = rawRes.status
            rawText = await rawRes.text()
        } catch (e2) {
            console.error("TokkenrBlockchain.fetch(): could not refetch raw response for txid=" + txid + ":", e2)
        }
        const raw100 = rawText.substring(0, 100)
        const errMess = "TokkenrBlockchain.fetch(): for txid=" + txid
            + " status=" + rawStatus
            + " first 100 chars: " + JSON.stringify(raw100)
            + (pullError ? " | pullError: " + pullError.message : "")
        console.error(errMess)
        throw new Error(errMess)
    }


    // version from the wrapper, beware it cannot take more params or they will be ignored!
    async utxos(script) {
        return await this.utxos_with_class_origin(script)
    }


    /**
     * BEWARE THE NODE DOES NOT INDEXES BSV ADDRESSES SO IF YOU TRY TO RETRIEVE UTXOS FROM A PURSE IT WILL ANSWER AN EMPTY ARRAY
     */
    async utxos_with_class_origin(script, class_origin = null) {
        const address = scriptHex_to_address(script)
        const function_id = this.classname + "utxos(script=" + script
            + ", address=" + address + ", class_origin=" + class_origin + "): "
        const log = getLog(function_id)

        // beware this.rundb_client.locations(script) returns an array of jig locations
        //         owned by the given hex script (less than # of utxos)
        // but utxos() must return an:
        //     <Array<{txid: string, vout: number, script: string, satoshis: number}>>

        let locations = null
        if (!class_origin) {
            locations = await this.rundb_client.locations(script)  // default run-db version with only script
        }
        else {  // fetch with address + class origin
            this._check_class_origin(class_origin)
            const scripthash = script_to_scripthash(script)
            const url = this.host_url + "/unspent?scripthash=" + scripthash + "&class=" + class_origin

            try {
                const fetch_data = await fetch(url, { timeout: 8000 })
                locations = await fetch_data.json()
                log("locations = ", locations)
            } catch (e) {
                const error_mess = function_id + "failed fetch for url=" + url + ":" + e
                console.error(error_mess)
                throw new Error(error_mess)
            }

            // Check the response
            if (!Array.isArray(locations) || locations.some(location => typeof location !== 'string')) {
                const error_mess = function_id + "Received invalid locations:" + JSON.stringify(locations)
                console.error(error_mess)
                throw new Error(error_mess)
            }
        }

        if (this.DEBUG) log("locations: (type", typeof locations, ", length", locations.length, "): ", locations)

        const utxos = []
        for (const location of locations) {
            if (!location.includes("_o")) {  // safety check
                const errormess = function_id + "location doesn't contain '_o': " + location
                console.error(errormess)
                throw new Error(errormess)
            }
            const jig_data = await this.rundb_client.pull("jig://" + location)
            let satoshis = null
            // beware testing without Number.isInteger() fails because satoshis=0 should return true
            if (jig_data && jig_data.props && Number.isInteger(jig_data.props.satoshis)) satoshis = jig_data.props.satoshis
            // beware even if we don't have satoshis we can have a token utxo
            const utxo = {
                txid: location.split("_o")[0],
                vout: parseInt(location.split("_o")[1]),
                script: script,
                // TODO since we await in the loop, we should do them in parallel (retrieve all the satoshis values then build 'utxos' array)
                satoshis: parseInt(satoshis),
            }
            const utxo_is_valid = _check_utxo_validity(utxo)
            if (utxo_is_valid) utxos.push(utxo)
            else {
                console.error(function_id + "found invalid utxo for utxo="
                    + JSON.stringify(utxo) + "and jig_data=", jig_data)
            }
        }

        if (this.DEBUG) log("utxos: (type", typeof utxos, ", length", utxos.length, "): ", JSON.stringify(utxos))
        return utxos || []  // we need to return empty array if there is no utxos for that address
    }


    _check_class_origin(class_origin) {
        // error handling of param class_origin
        const function_id = this.classname + "_check_class_origin(): with class_origin=" + class_origin + ": "

        let error_mess = null
        if (typeof class_origin !== 'string') {
            error_mess = function_id + "invalid type: class_origin should be string but got:" + typeof class_origin
        }
        else if (!class_origin.includes("_")) {
            error_mess = function_id + "invalid class_origin, should contain '_o' but got:" + class_origin
        }
        if (error_mess) {
            console.error(error_mess)
            throw new Error(error_mess)
        }
    }


    async time(txid) {
        const log = getLog(this.classname, "time(): with txid=" + txid)
        const res = await this.rundb_client.pull("time://" + txid)
        if (this.DEBUG) log("res=", res)

        return res
    }

    async spends(txid, vout) {
        const log = getLog(this.classname, "spends(): with txid=" + txid + "_o" + vout)
        if (this.DEBUG) log("called")

        let final_txid = txid
        if (!final_txid.includes("_o")) {
            final_txid = final_txid + "_o" + vout
        }
        let res
        try {
            res = await this.rundb_client.pull("spend://" + final_txid)
        } catch (e) {
            log("catched error: " + e)
        }
        if (!res) res = null  // absolutely need to return null not undefined in that case !
        if (this.DEBUG) log("res=", res)

        return res
    }
}


export default TokkenrBlockchain

function _check_utxos_validity(utxos) {
    // adapted from https://github.com/runonbitcoin/run-sdk/blob/main/lib/plugins/blockchain-wrapper.js#L243
    if (!Array.isArray(utxos) || utxos.some(utxo => !_check_utxo_validity(utxo))) return false
    else return true
}

function _check_utxo_validity(utxo) {
    const function_id = "_check_utxo_validity(): "
    if (Array.isArray(utxo)) {
        throw new Error(function_id + "received utxo of type: " + typeof utxo)
    }
    // adapted from https://github.com/runonbitcoin/run-sdk/blob/main/lib/plugins/blockchain-wrapper.js#L243
    const HEX_REGEX = /^(?:[a-fA-F0-9][a-fA-F0-9])*$/
    if (typeof utxo.txid !== 'string') return false
    if (utxo.txid.length !== 64) return false
    if (!HEX_REGEX.test(utxo.txid)) return false
    if (typeof utxo.vout !== 'number') return false
    if (!Number.isInteger(utxo.vout)) return false
    if (utxo.vout < 0) return false
    if (typeof utxo.script !== 'string') return false
    if (!HEX_REGEX.test(utxo.script)) return false
    if (typeof utxo.satoshis !== 'number') return false
    if (!Number.isInteger(utxo.satoshis)) return false
    if (utxo.satoshis < 0) return false
    return true
}


